import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/** Bulk-upload dialog for Leads.
 *  Two states — pick file, then upload → show summary + downloadable error report. */
export default function LeadsUploadDialog({ open, onOpenChange, onComplete }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setFile(null); setResult(null); setUploading(false);
  }, []);

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data?.ok && data?.summary?.valid > 0) {
        toast.success(`${data.summary.valid} lead${data.summary.valid === 1 ? "" : "s"} imported.`);
      } else if (!data?.ok) {
        toast.error(data?.errors?.[0]?.reason || "Upload failed");
      } else if (data?.summary?.valid === 0) {
        toast.warning("No valid rows in the file.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = async () => {
    if (!result?.import_id) return;
    try {
      const res = await api.get(`/leads/imports/${result.import_id}/errors`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `kippu-import-errors-${result.import_id.slice(0, 8)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download error report");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "kippu-leads-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  const s = result?.summary;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="upload-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-indigo-600" />
            Upload Leads
          </DialogTitle>
          <DialogDescription>
            Excel (.xlsx) or CSV — up to 16,000+ rows per file.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors"
            >
              <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400 mb-2" strokeWidth={1.5} />
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="upload-file-input"
                />
                <span className="inline-block px-3 py-1.5 bg-white border border-slate-300 rounded text-xs font-medium hover:bg-slate-50">
                  Choose file
                </span>
              </label>
              <div className="mt-2 text-[11px] text-slate-500">
                {file ? file.name : "No file selected"}
              </div>
            </div>
            <button
              type="button"
              className="text-[11px] text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
              onClick={downloadTemplate}
              data-testid="template-download-link"
            >
              Download the standard template
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="upload-result">
            {s?.valid > 0 ? (
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
                <CheckCircle2 className="w-4 h-4" />
                <span><strong>{s.valid}</strong> lead{s.valid === 1 ? "" : "s"} imported successfully.</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 text-xs">
                <AlertCircle className="w-4 h-4" />
                <span>No valid rows were imported.</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Stat label="Total rows" value={s?.total_rows ?? 0} />
              <Stat label="Imported" value={s?.valid ?? 0} tone="ok" />
              <Stat label="Invalid" value={s?.invalid ?? 0} tone={s?.invalid ? "warn" : ""} />
              <Stat label="Duplicates" value={(s?.duplicates_in_file ?? 0) + (s?.duplicates_existing ?? 0)} />
            </div>
            {result?.errors?.length > 0 && (
              <div className="max-h-40 overflow-auto border rounded text-[11px]">
                <table className="w-full">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1">Row</th>
                      <th className="text-left px-2 py-1">Column</th>
                      <th className="text-left px-2 py-1">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.slice(0, 40).map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono">{e.row}</td>
                        <td className="px-2 py-1">{e.column || "—"}</td>
                        <td className="px-2 py-1 text-slate-700">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {result?.errors_truncated && (
              <div className="text-[11px] text-slate-500">Only first 200 errors shown. Download the full report below.</div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {result ? (
            <>
              {(result?.errors?.length || 0) > 0 && (
                <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="download-errors">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Error Report
                </Button>
              )}
              <Button size="sm" onClick={() => { onComplete?.(); reset(); }} data-testid="upload-done">
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => handleClose(false)} data-testid="upload-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!file || uploading}
                onClick={handleUpload}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="upload-submit"
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const cls =
    tone === "ok" ? "text-emerald-700 bg-emerald-50" :
    tone === "warn" ? "text-amber-700 bg-amber-50" :
    "text-slate-700 bg-slate-50";
  return (
    <div className={`rounded border border-slate-200 px-2.5 py-2 ${cls}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold leading-tight">{value}</div>
    </div>
  );
}
